import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// ==================== DOCTOR MEDICINE MASTER ====================

export const createDoctorMedicine = createAsyncThunk(
    'medicine/createDoctorMedicine',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/doctor-medicine`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getDoctorMedicines = createAsyncThunk(
    'medicine/getDoctorMedicines',
    async ({ doctorId, page = 1, limit = 50, search = '', type = 'all' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/doctor-medicine/doctor/${doctorId}`,
                { params: { page, limit, search, type } }
            );
            return response.data;
        } catch (error) {
            // Keep the HTTP status next to the body: a 403 (not this doctor's
            // catalogue) is not the same thing as a failed load.
            if (error.response) {
                const data = error.response.data;
                return rejectWithValue({
                    ...(data && typeof data === 'object' ? data : { message: data || error.message }),
                    httpStatus: error.response.status,
                });
            }
            return rejectWithValue(error.message);
        }
    }
);

export const updateDoctorMedicine = createAsyncThunk(
    'medicine/updateDoctorMedicine',
    async ({ medicineId, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(
                `${baseURL}/doctor-medicine/${medicineId}`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteDoctorMedicine = createAsyncThunk(
    'medicine/deleteDoctorMedicine',
    async (medicineId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(
                `${baseURL}/doctor-medicine/${medicineId}`
            );
            return { ...response.data, medicineId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ==================== PICK FROM THE HOSPITAL PHARMACY ====================
// Owner, 12 Oct 2026: where the Super Admin set the tenant's medicines source
// to "From pharmacy" or "Both", a doctor picks items from the hospital
// pharmacy's own list onto theirs (Backend services/doctorMedicineSource.js).

export const getPharmacyItems = createAsyncThunk(
    'medicine/getPharmacyItems',
    async ({ doctorId, page = 1, limit = 30, search = '', type = 'all' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/doctor-medicine/pharmacy-items/doctor/${doctorId}`,
                { params: { page, limit, search, type } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const pickPharmacyItem = createAsyncThunk(
    'medicine/pickPharmacyItem',
    async ({ doctorId, pharmacyMedicineId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/doctor-medicine/pick`, { doctorId, pharmacyMedicineId });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ==================== APPOINTMENT PRESCRIPTION ====================

export const createPrescription = createAsyncThunk(
    'medicine/createPrescription',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `${baseURL}/appointment-prescription`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getPrescriptionsByAppointment = createAsyncThunk(
    'medicine/getPrescriptionsByAppointment',
    async (appointmentId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/appointment-prescription/appointment/${appointmentId}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updatePrescription = createAsyncThunk(
    'medicine/updatePrescription',
    async ({ prescriptionId, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(
                `${baseURL}/appointment-prescription/${prescriptionId}`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deletePrescription = createAsyncThunk(
    'medicine/deletePrescription',
    async (prescriptionId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(
                `${baseURL}/appointment-prescription/${prescriptionId}`
            );
            return { ...response.data, prescriptionId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Which page a getDoctorMedicines call asked for (page 1 replaces the list,
// any later page appends to it — "Load more").
const requestedPage = (action) => Number(action.meta?.arg?.page) || 1;

const medicineSlice = createSlice({
    name: 'medicine',
    initialState: {
        // ---- the doctor's catalogue (server-side search + paging) ----
        medicines: [],
        loading: false,          // page 1 of a (new) query is on its way
        loadingMore: false,      // a later page is on its way
        medicinesPage: 0,
        medicinesTotalPages: 0,
        medicinesTotal: 0,
        medicinesError: null,    // page 1 failed: the list is NOT known
        loadMoreError: null,     // a later page failed: what is shown is still right
        medicinesRequestId: null,
        // Where this doctor's list comes from — 'manual' | 'pharmacy' | 'both'
        // (the tenant's medicines source, sent with every list page).
        medicinesSource: 'manual',
        submitting: false,
        deletingId: null,
        // ---- one appointment's prescription ----
        prescriptions: [],
        // The appointment `prescriptions` belongs to. A screen showing another
        // appointment must never render these rows.
        prescriptionsFor: null,
        prescriptionsLoading: false,
        prescriptionsError: null,
        error: null,
    },
    reducers: {
        clearMedicineError: (state) => {
            state.error = null;
        },
        clearPrescriptions: (state) => {
            state.prescriptions = [];
            state.prescriptionsFor = null;
            state.prescriptionsError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ---- master list ----
            .addCase(getDoctorMedicines.pending, (state, action) => {
                // The newest request wins: a slow answer to "par" must not
                // overwrite the answer to "paracetamol".
                state.medicinesRequestId = action.meta.requestId;
                if (requestedPage(action) > 1) {
                    state.loadingMore = true;
                    state.loadMoreError = null;
                } else {
                    state.loading = true;
                    state.loadingMore = false;
                    state.medicinesError = null;
                    state.loadMoreError = null;
                }
                state.error = null;
            })
            .addCase(getDoctorMedicines.fulfilled, (state, action) => {
                if (state.medicinesRequestId && action.meta.requestId !== state.medicinesRequestId) return;
                const page = requestedPage(action);
                const rows = action.payload?.data || [];
                if (page > 1) {
                    const seen = new Set(state.medicines.map((m) => m.medicineId));
                    state.medicines = [...state.medicines, ...rows.filter((m) => !seen.has(m.medicineId))];
                } else {
                    state.medicines = rows;
                }
                state.loading = false;
                state.loadingMore = false;
                state.medicinesSource = action.payload?.source || 'manual';
                state.medicinesPage = Number(action.payload?.page) || page;
                state.medicinesTotalPages = Number(action.payload?.totalPages) || (rows.length ? page : 0);
                state.medicinesTotal = Number(action.payload?.totalItems ?? action.payload?.total) || state.medicines.length;
            })
            .addCase(getDoctorMedicines.rejected, (state, action) => {
                if (state.medicinesRequestId && action.meta.requestId !== state.medicinesRequestId) return;
                state.loading = false;
                state.loadingMore = false;
                if (requestedPage(action) > 1) {
                    state.loadMoreError = action.payload ?? action.error?.message ?? 'failed';
                } else {
                    // A failed load is not an empty catalogue: drop whatever an
                    // earlier query left behind so it is never shown under this one.
                    state.medicines = [];
                    state.medicinesPage = 0;
                    state.medicinesTotalPages = 0;
                    state.medicinesTotal = 0;
                    state.medicinesError = action.payload ?? action.error?.message ?? 'failed';
                }
                state.error = action.payload;
            })
            .addCase(createDoctorMedicine.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(createDoctorMedicine.fulfilled, (state, action) => {
                state.submitting = false;
                if (action.payload?.data) {
                    state.medicines.unshift(action.payload.data);
                    state.medicinesTotal = (Number(state.medicinesTotal) || 0) + 1;
                }
            })
            .addCase(createDoctorMedicine.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })
            .addCase(pickPharmacyItem.fulfilled, (state, action) => {
                const row = action.payload?.data;
                if (!row) return;
                const idx = state.medicines.findIndex((m) => m.medicineId === row.medicineId);
                // Adopted = an entry already on the list now follows the pharmacy.
                if (idx !== -1) state.medicines[idx] = row;
                else {
                    state.medicines.unshift(row);
                    state.medicinesTotal = (Number(state.medicinesTotal) || 0) + 1;
                }
            })
            .addCase(updateDoctorMedicine.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(updateDoctorMedicine.fulfilled, (state, action) => {
                state.submitting = false;
                const updated = action.payload?.data;
                if (updated) {
                    const idx = state.medicines.findIndex(
                        (m) => m.medicineId === updated.medicineId
                    );
                    if (idx !== -1) state.medicines[idx] = updated;
                }
            })
            .addCase(updateDoctorMedicine.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })
            .addCase(deleteDoctorMedicine.pending, (state, action) => {
                state.deletingId = action.meta.arg;
                state.error = null;
            })
            .addCase(deleteDoctorMedicine.fulfilled, (state, action) => {
                state.deletingId = null;
                const id = action.payload?.medicineId || action.payload?.data?.medicineId;
                if (id) {
                    const before = state.medicines.length;
                    state.medicines = state.medicines.filter((m) => m.medicineId !== id);
                    if (state.medicines.length < before && state.medicinesTotal > 0) state.medicinesTotal -= 1;
                }
            })
            .addCase(deleteDoctorMedicine.rejected, (state, action) => {
                state.deletingId = null;
                state.error = action.payload;
            })

            // ---- prescriptions ----
            .addCase(getPrescriptionsByAppointment.pending, (state, action) => {
                // Switching appointment empties the table at once — the previous
                // visit's medicines are never on screen under the next one.
                if (state.prescriptionsFor !== action.meta.arg) state.prescriptions = [];
                state.prescriptionsFor = action.meta.arg;
                state.prescriptionsLoading = true;
                state.prescriptionsError = null;
                state.error = null;
            })
            .addCase(getPrescriptionsByAppointment.fulfilled, (state, action) => {
                // An answer for an appointment that is no longer open is dropped.
                if (action.meta.arg !== state.prescriptionsFor) return;
                state.prescriptionsLoading = false;
                state.prescriptions = action.payload?.data || [];
            })
            .addCase(getPrescriptionsByAppointment.rejected, (state, action) => {
                if (action.meta.arg !== state.prescriptionsFor) return;
                state.prescriptionsLoading = false;
                state.prescriptions = [];
                state.prescriptionsError = action.payload ?? action.error?.message ?? 'failed';
                state.error = action.payload;
            })
            .addCase(createPrescription.fulfilled, (state, action) => {
                const row = action.payload?.data;
                if (row && (!state.prescriptionsFor || !row.appointmentId || row.appointmentId === state.prescriptionsFor)) {
                    state.prescriptions.push(row);
                }
            })
            .addCase(updatePrescription.fulfilled, (state, action) => {
                const updated = action.payload?.data;
                if (updated) {
                    const idx = state.prescriptions.findIndex(
                        (p) => p.prescriptionId === updated.prescriptionId
                    );
                    if (idx !== -1) state.prescriptions[idx] = updated;
                }
            })
            .addCase(deletePrescription.fulfilled, (state, action) => {
                const id = action.payload?.prescriptionId || action.payload?.data?.prescriptionId;
                if (id) {
                    state.prescriptions = state.prescriptions.filter(
                        (p) => p.prescriptionId !== id
                    );
                }
            });
    },
});

export const { clearMedicineError, clearPrescriptions } = medicineSlice.actions;
export default medicineSlice.reducer;
